import { trimAndNullifyEmpty } from '@/util/zod/transforms'
import { parseTimestamp } from '@/util/zod/validators'
import { z } from 'zod'

export enum MatterType {
  STANDARD = 'STANDARD',
  SAMPLE = 'SAMPLE',
  LEGACY_UNASSIGNED = 'LEGACY_UNASSIGNED',
}

export const MatterTypeEnum = z.nativeEnum(MatterType)

export enum MatterViewerRole {
  Owner = 'owner',
  Collaborator = 'collaborator',
}

export const MatterViewerRoleEnum = z.nativeEnum(MatterViewerRole)

const nullableTimestamp = z.preprocess((val, ctx) => (val === null ? null : parseTimestamp(val, ctx)), z.number().nullable())

export const MatterReadSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string(),
  name: z.string(),
  client: z.string().nullable(),
  description: z.string().nullable(),
  default_jurisdiction: z.string().nullable(),
  type: MatterTypeEnum,
  created_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number()),
  updated_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number()),
  /**
   * Note: In practice, last_activity_at will always be provided by the backend.
   * However, the field is nullable in the BE schema to account for snapshots of records
   * taken in the past, so we handle the null case for safety.
   */
  last_activity_at: nullableTimestamp,
  // Collaboration fields — .catch() fallbacks keep the existing API working until BE ships
  viewer_role: MatterViewerRoleEnum.catch(MatterViewerRole.Owner),
  member_count: z.number().int().catch(1),
  // Populated only for collaborator rows; null for owner rows
  invited_at: nullableTimestamp.catch(null),
  invited_by_user_id: z.string().nullable().catch(null),
  accepted_at: nullableTimestamp.catch(null),
})

export type MatterRead = z.infer<typeof MatterReadSchema>

// ---- Member schemas ----

export enum OrgRole {
  Admin = 'admin',
  User = 'user',
}

export const OrgRoleEnum = z.nativeEnum(OrgRole)

export const MatterMemberSchema = z.object({
  user_id: z.string(),
  display_name: z.string().nullable(),
  email: z.string().nullable(),
  org_role: OrgRoleEnum.nullable(),
  matter_role: MatterViewerRoleEnum,
  invited_at: nullableTimestamp,
  accepted_at: nullableTimestamp,
})

export type MatterMember = z.infer<typeof MatterMemberSchema>

export const GetMatterMembersResponseSchema = z.object({
  members: z.array(MatterMemberSchema),
})

/**
 * MatterRead with nullable fields transformed to undefined for table sorting.
 * This allows TanStack Table's `sortUndefined: 'last'` option to work correctly,
 * keeping blank values at the bottom regardless of sort direction.
 */
export type MatterReadForTable = Omit<MatterRead, 'client' | 'description' | 'last_activity_at'> & {
  client: string | undefined
  description: string | undefined
  last_activity_at: number | undefined
}

export const GetUserMattersResponseSchema = z.object({
  matters: z.array(MatterReadSchema),
})

export type GetUserMattersResponse = z.infer<typeof GetUserMattersResponseSchema>

export const CreateOrUpdateMatterSchema = z.object({
  // Trim whitespace and validate that name is a non-empty string
  name: z.preprocess(
    (val) => (typeof val === 'string' ? val.trim() : val),
    z.string().min(1, 'This is a required field').max(500, 'Matter name must not exceed 500 characters')
  ),

  // Optional fields: trim whitespace and convert empty strings to null
  client: z.string().nullable().transform(trimAndNullifyEmpty),
  description: z.string().nullable().transform(trimAndNullifyEmpty),
  default_jurisdiction: z.string().nullable().optional(),
})

export type CreateOrUpdateMatterForm = z.infer<typeof CreateOrUpdateMatterSchema>
